/**
 * Server refusals, mapped to a sentence in the reader's language and — where
 * one exists — a way out.
 *
 * The clinic-creation resolver used to wrap every failure in one Persian
 * string: `new Error('ایجاد کلینیک با خطا مواجه شد')`. Three separate faults
 * arrived through it looking identical — a name the service rejected, a clinic
 * id that already exists, and the accounting service being down — and an
 * English-speaking user read Persian regardless of what they had chosen. None
 * of them could be acted on, so all three became support calls.
 *
 * The BFF now throws a `GraphQLError` carrying `extensions.code`. THIS module
 * owns the copy, because the BFF must not: it serves three languages and a
 * resolver cannot know which one the caller reads.
 *
 * `recovery` names what the screen should offer. It is deliberately a key
 * rather than a component — the same code means different actions on different
 * screens (a duplicate clinic offers «go to your clinic» in the wizard and
 * nothing at all in a background refresh), so the screen decides, and this file
 * only says what kind of way out exists.
 */

export const RECOVERY = {
  /** Nothing but "try again" — a transient fault. */
  RETRY: 'retry',
  /** The name field is wrong; focus it. */
  FIELD_NAME: 'field:name',
  /** They already have this clinic; offer to enter it. */
  GO_TO_CLINIC: 'go-to-clinic',
  /** A half-applied saga; offer `repairClinicSetup`. */
  REPAIR: 'repair',
  /** Their grants are stale; offer the access refresh, not a re-submit. */
  REFRESH_ACCESS: 'refresh-access',
  /** Nothing the person can do here. */
  NONE: 'none',
};

const CREATE_CLINIC = {
  CLINIC_NAME_INVALID: { key: 'errors.clinic.nameInvalid', recovery: RECOVERY.FIELD_NAME },
  CLINIC_ALREADY_EXISTS: { key: 'errors.clinic.alreadyExists', recovery: RECOVERY.GO_TO_CLINIC },
  CLINIC_SETUP_INCOMPLETE: { key: 'errors.clinic.setupIncomplete', recovery: RECOVERY.REPAIR },
  // Listed although it is the fallback's meaning too: an UNLISTED code shows the
  // server's own sentence, which is Persian whatever language the panel is in.
  CLINIC_CREATE_FAILED: { key: 'errors.clinic.createFailed', recovery: RECOVERY.RETRY },
  SERVICE_UNAVAILABLE: { key: 'errors.server.unreachable', recovery: RECOVERY.RETRY },
  FORBIDDEN: { key: 'errors.access.denied', recovery: RECOVERY.NONE },
  // A currency outside the allow-list (docs/clinic-currency/CONTRACTS.md §1). The
  // picker offers only allowed codes, so this is a stale page, not a typo.
  INVALID_CURRENCY: { key: 'errors.currency.invalid', recovery: RECOVERY.NONE },
  // A country that is not an assigned ISO 3166-1 code (PM-0082). Like the
  // currency, the picker offers only real codes, so this is a stale page.
  INVALID_COUNTRY: { key: 'errors.country.invalid', recovery: RECOVERY.NONE },
};

const ROLES = {
  ROLE_NAME_INVALID: { key: 'errors.roles.nameInvalid', recovery: RECOVERY.NONE },
  // A role that already exists is the retry path working as intended, never an
  // error the person should read. The seeder treats it as success; it is listed
  // so a caller that does surface it says something true.
  ROLE_EXISTS: { key: 'errors.roles.exists', recovery: RECOVERY.NONE },
  // The token predates the clinic. Re-running the mutation cannot fix that, and
  // doing so is how the roles step ended up creating duplicates — so this one
  // routes to the access refresh instead of to a retry.
  FORBIDDEN: { key: 'errors.access.stale', recovery: RECOVERY.REFRESH_ACCESS },
  SERVICE_UNAVAILABLE: { key: 'errors.server.unreachable', recovery: RECOVERY.RETRY },
};

const JOIN = {
  INVITE_INVALID: { key: 'errors.invite.notFoundOrExpired', recovery: RECOVERY.NONE },
  ALREADY_MEMBER: { key: 'errors.invite.alreadyMember', recovery: RECOVERY.GO_TO_CLINIC },
  ALREADY_REQUESTED: { key: 'errors.invite.alreadyRequested', recovery: RECOVERY.NONE },
  SUSPENDED_MEMBER: { key: 'errors.invite.suspended', recovery: RECOVERY.NONE },
  // The two halves of the name the invitation page asks for (PM-0453). The page
  // checks them first, so these answer a stale page; the form stays open.
  NAME_REQUIRED: { key: 'errors.invite.nameRequired', recovery: RECOVERY.RETRY },
  NAME_TOO_LONG: { key: 'errors.invite.nameTooLong', recovery: RECOVERY.RETRY },
  SERVICE_UNAVAILABLE: { key: 'errors.server.unreachable', recovery: RECOVERY.RETRY },
};

const TABLES = {
  createClinic: { table: CREATE_CLINIC, fallback: 'errors.clinic.createFailed' },
  roles: { table: ROLES, fallback: 'errors.roles.seedFailed' },
  join: { table: JOIN, fallback: 'errors.invite.joinFailed' },
};

/**
 * `{ message, recovery, code, clinicId }` for a thrown error.
 *
 * The server's own `message` is used ONLY when there is no code — a coded
 * refusal is a refusal we have written copy for, and preferring the wire's
 * Persian over that copy is how an English UI leaks Persian back in. An
 * uncoded one is something nobody anticipated, where the server's sentence,
 * however rough, carries more than a generic fallback would.
 */
export function describeError(err, kind, t) {
  const { table, fallback } = TABLES[kind] || TABLES.createClinic;
  const code = err?.extensions?.code || '';
  const hit = table[code];
  if (hit) {
    return {
      code,
      message: t(hit.key),
      recovery: hit.recovery,
      clinicId: err?.extensions?.clinicId || '',
    };
  }
  return {
    code,
    message: err?.message || t(fallback),
    recovery: RECOVERY.RETRY,
    clinicId: err?.extensions?.clinicId || '',
  };
}

export default describeError;
